/**
 * ACTIVITY-CORE — history entries list (shared by the "Activity"
 * page and the entity detail "History" sheet).
 * Data-source agnostic component: receives already loaded rows.
 */
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import {
  ArchiveRestore,
  Banknote,
  ChefHat,
  CloudOff,
  type LucideIcon,
  Pencil,
  Percent,
  Plus,
  RotateCcw,
  Trash2,
  Undo2,
  Vault,
  XCircle,
} from "@fleet/icons";
import { type ActivityRow, groupActivityByBatch, isOfflineEdit } from "@malla/shared";
import { api } from "@/lib/api";
import { showToast } from "@/components/ui/toast";
import { Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { useI18n } from "@/providers/i18n-provider";
import {
  activityReason,
  activitySemanticKey,
  activitySentence,
  ENTITY_LABELS,
  formatRelativeTime,
} from "../lib/activity-text";

const ACTION_ICONS: Record<string, LucideIcon> = {
  created: Plus,
  updated: Pencil,
  trashed: Trash2,
  purged: Trash2,
  deleted: Trash2,
  restored: ArchiveRestore,
  reverted: RotateCcw,
  sent_kitchen: ChefHat,
  order_paid: Banknote,
  order_voided: XCircle,
  order_reopened: Undo2,
  discount_applied: Percent,
  discount_removed: Percent,
  line_voided: XCircle,
  line_restored: ArchiveRestore,
  payment_taken: Banknote,
  payment_voided: XCircle,
  register_opened: Vault,
  register_closed: Vault,
  cash_in: Banknote,
  cash_out: Banknote,
  cash_refund: Banknote,
};

const ACTION_TONES: Record<string, string> = {
  created: "bg-success/10 text-success",
  order_paid: "bg-success/10 text-success",
  payment_taken: "bg-success/10 text-success",
  register_opened: "bg-success/10 text-success",
  cash_in: "bg-success/10 text-success",
  updated: "bg-primary/10 text-primary",
  sent_kitchen: "bg-primary/10 text-primary",
  order_moved_table: "bg-primary/10 text-primary",
  restored: "bg-primary/10 text-primary",
  line_restored: "bg-primary/10 text-primary",
  register_closed: "bg-primary/10 text-primary",
  order_voided: "bg-destructive/10 text-destructive",
  line_voided: "bg-destructive/10 text-destructive",
  payment_voided: "bg-destructive/10 text-destructive",
  discount_applied: "bg-warning/10 text-warning",
  discount_removed: "bg-warning/10 text-warning",
  order_reopened: "bg-warning/10 text-warning",
  cash_out: "bg-warning/10 text-warning",
  cash_refund: "bg-warning/10 text-warning",
  trashed: "bg-destructive/10 text-destructive",
  purged: "bg-destructive/10 text-destructive",
  deleted: "bg-destructive/10 text-destructive",
  reverted: "bg-muted text-muted-foreground",
};

interface ActivityListProps {
  rows: () => ActivityRow[];
  isLoading?: () => boolean;
  /** Group consecutive entries from the same batch (general feed).*/
  grouped?: boolean;
  /** Active user's id (for "You").*/
  currentUserId?: () => string | undefined;
  /** Shows the "Undo this change" button per entry. */
  canRevert?: boolean;
}

function ActivityEntryRow(props: {
  row: ActivityRow;
  currentUserId?: string;
  canRevert?: boolean;
}) {
  const { t, locale } = useI18n();
  const [reverting, setReverting] = createSignal(false);

  const semanticKey = () => activitySemanticKey(props.row);
  const Icon = () => ACTION_ICONS[semanticKey()] ?? Pencil;
  const tone = () => ACTION_TONES[semanticKey()] ?? "bg-muted text-muted-foreground";

  const actorLabel = () => {
    if (props.row.actor_user_id && props.row.actor_user_id === props.currentUserId) {
      return t(msg`You`);
    }
    return props.row.actor_name ?? t(msg`Someone`);
  };

  const sentence = () => {
    const { descriptor, values } = activitySentence(props.row);
    return t(descriptor, values);
  };

  const entityLabel = () => {
    const descriptor = ENTITY_LABELS[props.row.entity_type ?? ""];
    return descriptor ? t(descriptor) : (props.row.entity_type ?? "");
  };

  const reason = () => activityReason(props.row);

  const serverTime = () =>
    props.row.server_ts ? new Date(props.row.server_ts).toLocaleString(locale()) : "";
  const clientTime = () =>
    props.row.client_ts ? new Date(props.row.client_ts).toLocaleString(locale()) : "";

  const handleRevert = async () => {
    if (reverting()) {
      return;
    }
    setReverting(true);
    try {
      const { error } = await api.POST("/api/activity/{id}/revert", {
        params: { path: { id: props.row.id } },
      });
      if (error) {
        showToast(error.message || t(msg`Could not undo this change`), "error");
      } else {
        showToast(t(msg`Change undone`), "success");
      }
    } catch {
      showToast(t(msg`Could not undo this change`), "error");
    } finally {
      setReverting(false);
    }
  };

  return (
    <div class="group flex items-start gap-3 py-2.5" data-testid="activity-entry">
      <div
        class={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tone()}`}
        aria-hidden="true"
      >
        {(() => {
          const IconComp = Icon();
          return <IconComp class="h-4 w-4" />;
        })()}
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-sm leading-snug">
          <span class="font-semibold" data-testid="activity-actor">
            {actorLabel()}
          </span>{" "}
          <span data-testid="activity-sentence">{sentence()}</span>
        </p>
        <Show when={reason()}>
          {/* Motivo de negocio (anulación/descuento): antifraude visible.*/}
          <p class="mt-0.5 text-xs italic text-muted-foreground" data-testid="activity-reason">
            {/* oxlint-disable-next-line lingui/no-single-variables-to-translate -- la frase tiene texto ("Reason:"); el placeholder viene del patch*/}
            {t(msg`Reason: {reason}`, { reason: reason() ?? "" })}
          </p>
        </Show>
        <p class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          {/* server_ts is the canonical order; title shows exact timestamp.*/}
          <time title={serverTime()} data-testid="activity-server-ts">
            {formatRelativeTime(props.row.server_ts, locale())}
          </time>
          <span class="opacity-70">{entityLabel()}</span>
          <Show when={isOfflineEdit(props.row)}>
            {/* Honest secondary info: performed offline at client_ts and arrived later.*/}
            <span
              class="inline-flex items-center gap-1 rounded-full bg-warning/10 px-1.5 py-0.5 text-warning"
              title={t(msg`Done offline on {date}, synced later`, { date: clientTime() })}
              data-testid="activity-offline-badge"
            >
              <CloudOff class="h-3 w-3" />
              {t(msg`Offline`)}
            </span>
          </Show>
          <Show when={props.row.platform}>
            <span class="uppercase tracking-wide opacity-70">{props.row.platform}</span>
          </Show>
        </p>
      </div>
      <Show when={props.canRevert}>
        <button
          type="button"
          class="shrink-0 rounded-md border border-border/40 px-2 py-1 text-xs font-medium text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-40"
          onClick={() => void handleRevert()}
          disabled={reverting()}
          data-testid="activity-revert"
          title={t(msg`Undo this change`)}
        >
          <span class="inline-flex items-center gap-1">
            <RotateCcw class="h-3 w-3" />
            {t(msg`Undo this change`)}
          </span>
        </button>
      </Show>
    </div>
  );
}

function ActivityListSkeleton() {
  return (
    <div class="space-y-3" data-testid="activity-skeleton">
      <For each={[0, 1, 2, 3]}>
        {() => (
          <div class="flex items-center gap-3 py-2">
            <Skeleton class="h-8 w-8 animate-shimmer rounded-full" />
            <div class="flex-1 space-y-1.5">
              <Skeleton class="h-3.5 w-3/4 animate-shimmer rounded" />
              <Skeleton class="h-3 w-1/3 animate-shimmer rounded" />
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

export function ActivityList(props: ActivityListProps) {
  const { t } = useI18n();

  const groups = () => groupActivityByBatch(props.rows());

  return (
    <Show when={!props.isLoading?.()} fallback={<ActivityListSkeleton />}>
      <Show
        when={props.rows().length > 0}
        fallback={
          <EmptyState
            illustration="orders"
            title={t(msg`No activity yet`)}
            description={t(msg`Changes in your restaurant will show up here.`)}
          />
        }
      >
        <Show
          when={props.grouped}
          fallback={
            <div class="divide-y divide-border/20" data-testid="activity-list">
              <For each={props.rows()}>
                {(row) => (
                  <ActivityEntryRow
                    row={row}
                    currentUserId={props.currentUserId?.()}
                    canRevert={props.canRevert}
                  />
                )}
              </For>
            </div>
          }
        >
          <div class="space-y-3" data-testid="activity-list">
            <For each={groups()}>
              {(group) => (
                <div class="rounded-xl border border-border/30 bg-card/60 px-4 py-1.5">
                  <Show when={group.entries.length > 1}>
                    <p class="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t(msg`{count} changes together`, { count: String(group.entries.length) })}
                    </p>
                  </Show>
                  <div class="divide-y divide-border/15">
                    <For each={group.entries}>
                      {(row) => (
                        <ActivityEntryRow
                          row={row}
                          currentUserId={props.currentUserId?.()}
                          canRevert={props.canRevert}
                        />
                      )}
                    </For>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </Show>
  );
}
